import {
  API_CACHE_KEYS,
  ApiResponseCache,
} from '../../redux/api-response-cache';
import { IUser } from '../../types/interfaces/user';
import { logAuth } from './auth-debug-logger';

const BACKEND_SESSION_CACHE_KEY = 'auth/backend-session';

interface IBackendSessionCache {
  uid: string;
  signedInAt: number;
}

let activeFirebaseUid: string | null = null;

export function setActiveFirebaseUid(uid: string | null): void {
  activeFirebaseUid = uid;
}

export function getActiveFirebaseUid(): string | null {
  return activeFirebaseUid;
}

export function clearUserScopedApiCaches(): void {
  logAuth('cache:clearUserScoped');
  ApiResponseCache.remove(API_CACHE_KEYS.ME);
  ApiResponseCache.remove(API_CACHE_KEYS.ME_DETAILED);
  ApiResponseCache.remove(API_CACHE_KEYS.AVAILABLE_EXERCISES);
  ApiResponseCache.remove(API_CACHE_KEYS.AVAILABLE_DETAILED_EXERCISES);
  ApiResponseCache.remove(API_CACHE_KEYS.EXERCISE_OPTIONS);
  ApiResponseCache.remove(BACKEND_SESSION_CACHE_KEY);
}

function getBackendSessionCache(): IBackendSessionCache | null {
  return ApiResponseCache.get<IBackendSessionCache>(BACKEND_SESSION_CACHE_KEY);
}

function isMeCacheOwnedByFirebaseUid(me: IUser, firebaseUid: string): boolean {
  return !me.uid || me.uid === firebaseUid;
}

/** Drops persisted user caches when they belong to another Firebase account. */
export function reconcileUserCachesForFirebaseUid(
  firebaseUid: string | null,
): void {
  if (!firebaseUid) {
    clearUserScopedApiCaches();
    return;
  }

  const session = getBackendSessionCache();
  const me = ApiResponseCache.get<IUser>(API_CACHE_KEYS.ME);
  const sessionMismatch = !!session && session.uid !== firebaseUid;
  const meMismatch = !!me && !isMeCacheOwnedByFirebaseUid(me, firebaseUid);

  if (sessionMismatch || meMismatch) {
    logAuth('cache:reconcileMismatch', {
      firebaseUid,
      sessionMismatch,
      meMismatch,
    });
    clearUserScopedApiCaches();
  }
}

export function setBackendSessionCache(uid: string): void {
  ApiResponseCache.set<IBackendSessionCache>(BACKEND_SESSION_CACHE_KEY, {
    uid,
    signedInAt: Date.now(),
  });
}

export function clearBackendSessionCache(): void {
  ApiResponseCache.remove(BACKEND_SESSION_CACHE_KEY);
}

export function getCachedMeForActiveFirebaseUser(): IUser | null {
  if (!activeFirebaseUid) {
    return null;
  }

  const session = getBackendSessionCache();
  if (!session || session.uid !== activeFirebaseUid) {
    return null;
  }

  const me = ApiResponseCache.get<IUser>(API_CACHE_KEYS.ME);
  if (!me || !isMeCacheOwnedByFirebaseUid(me, activeFirebaseUid)) {
    return null;
  }

  return me;
}

export function canUseOptimisticBackendSession(uid: string): boolean {
  reconcileUserCachesForFirebaseUid(uid);

  const session = getBackendSessionCache();
  if (!session || session.uid !== uid) {
    logAuth('cache:optimisticDenied', {
      uid,
      hasSession: !!session,
      sessionUid: session?.uid,
    });
    return false;
  }

  const canUse = getCachedMeForActiveFirebaseUser() != null;
  logAuth('cache:optimisticCheck', {
    uid,
    canUse,
    signedInAt: session.signedInAt,
  });
  return canUse;
}
